import React from "react";

import { SpinnerIcon, UsersIcon } from "@/components/icons";
import { PLATFORM_REGISTRY } from "@/config/platforms";

import { useFollowerHistory } from "../hooks/useFollowerHistory";
import { addDays, formatRanges, previousRange, rangeOf } from "../utils/dayAxis";
import { followerWindow } from "../utils/followerWindow";
import { windowDays, type WindowPeriod } from "../utils/period";
import {
  ReportHeadline,
  ReportPanel,
  ReportPanelEmpty,
  ReportPanelNote,
  ReportStat,
  ReportStats,
} from "./ReportPanel";
import { QuietList, SOURCE_COLOR, SourceBadge } from "./ReportViz";

const icon = (
  <SourceBadge color={SOURCE_COLOR.social}>
    <UsersIcon className="w-3.5 h-3.5" />
  </SourceBadge>
);

const signed = (n: number) => `${n > 0 ? "+" : ""}${n.toLocaleString()}`;

const LoadingRow: React.FC = () => (
  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
    <SpinnerIcon className="w-4 h-4" />
    Loading followers…
  </div>
);

/** Followers over a window, from the nightly snapshots: per platform the
 *  count at the window's end (yesterday) and the net change over the window
 *  beside the window before. A change whose baseline has no snapshot is
 *  withheld ("no comparison"), never computed on a part. */
export const FollowerWindowPanel: React.FC<{ clientId: string; period: WindowPeriod; today: string }> = ({
  clientId,
  period,
  today,
}) => {
  const span = rangeOf(addDays(today, -1), windowDays(period));
  const before = previousRange(span);
  // A week before the window before, so each baseline finds a snapshot a few missed nights back.
  const { data, isLoading, error } = useFollowerHistory(clientId, { since: addDays(before.since, -7), until: span.until });
  const summary = formatRanges(span, before);

  if (isLoading) {
    return (
      <ReportPanel title="Followers" icon={icon} summary={summary} collapsedBody={<LoadingRow />}>
        <LoadingRow />
      </ReportPanel>
    );
  }
  const rows = followerWindow(data, span).filter((row) => row.count != null || row.change != null);
  if (error || rows.length === 0) {
    const message = error ? "Follower history is unavailable." : "No follower snapshots in these days.";
    return (
      <ReportPanel title="Followers" icon={icon} summary={summary} collapsedBody={<ReportPanelNote>{message}</ReportPanelNote>}>
        <ReportPanelEmpty message={message} hint="Follower counts are snapshotted nightly for each connected platform." />
      </ReportPanel>
    );
  }
  const labelOf = (platform: string) => PLATFORM_REGISTRY[platform]?.label ?? platform;
  const stat = (row: (typeof rows)[number]) => (
    <ReportStat
      key={row.platform}
      label={labelOf(row.platform)}
      value={row.count != null ? row.count.toLocaleString() : "-"}
      note={row.change != null ? `${signed(row.change)} in these days` : undefined}
    />
  );

  // Folded: every platform's followers together, as Month folds them, the change summed only when each platform
  // has one.
  const total = rows.reduce((sum, row) => sum + (row.count ?? 0), 0);
  const totalChange = rows.every((row) => row.change != null)
    ? rows.reduce((sum, row) => sum + (row.change ?? 0), 0)
    : null;
  const headline = (
    <ReportHeadline
      stat={
        <ReportStat
          size="lg"
          label="Followers, all platforms"
          value={total.toLocaleString()}
          change={
            totalChange != null
              ? {
                  value: totalChange,
                  percentage: total - totalChange > 0 ? (totalChange / (total - totalChange)) * 100 : null,
                }
              : undefined
          }
        />
      }
    />
  );

  return (
    <ReportPanel
      title="Followers"
      icon={icon}
      summary={summary}
      headlineLabel="Followers, all platforms"
      collapsedBody={headline}
    >
      <div className="space-y-5">
        <ReportStats>{rows.map(stat)}</ReportStats>
        <QuietList
          rows={rows.map((row) => ({
            label: `${labelOf(row.platform)}, net change`,
            value: row.change != null ? signed(row.change) : "-",
            aside: (
              <span className="text-[12px] text-gray-500 tabular-nums">
                {row.previousChange != null ? `vs ${signed(row.previousChange)} before` : "no comparison"}
              </span>
            ),
          }))}
        />
      </div>
    </ReportPanel>
  );
};
